import appConfig from "../application/appConfig";
import { Session } from "../framework/session/session";
import { UserPermissionScope } from "../models/common/enums";
import { refreshAuthentication } from "./tokenService";
import fetch from "cross-fetch";

type method =
    | "GET"
    | "POST"
    | "PUT"
    | "DELETE"

export interface ApiResponse<T> {
    status: number;
    statusText: string;
    data: T;
}

function handleGetInit<TParams = unknown>(url: string, data: TParams): string {
    const urlParams = new URLSearchParams();

    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            const value = data[key];

            if (value === undefined) {
                continue;
            }

            if (Array.isArray(value)) {
                value.forEach((item: string | number | Date) => {
                    urlParams.append(key, item.toString());
                });
            } else {
                // eslint-disable-next-line  @typescript-eslint/no-base-to-string
                urlParams.append(key, value.toString());
            }
        }
    }

    if (!urlParams.keys().next().done) {
        url += "?" + urlParams.toString();
    }

    return url;
}

/**
 * Light fetch wrapper for api.
 */
export async function apiCall<TResponse = unknown, TParams = unknown>(url: string, method: method, data?: TParams, signal?: AbortSignal): Promise<ApiResponse<TResponse>> {
    if (!appConfig.apiRootUrl) {
        throw new Error("appConfig.apiRootUrl not defined");
    }

    if (method === "GET" && data) {
        url = handleGetInit(url, data);
    }

    await refreshAuthentication();
    const res = await fetch(`${appConfig.apiRootUrl}api/${url}`, {
        method,
        body: (method !== "GET" && data) ? JSON.stringify(data) : null,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${await Session.getAccessToken()}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept",
            "Scope": UserPermissionScope.Mobile.toString(),
        },
        signal,
    });

    const isJson = res.headers.get("content-type")?.includes("application/json");

    if (!res.ok) {
        throw isJson ? await res.json() : await res.text();
    }

    return {
        status: res.status,
        statusText: res.statusText,
        data: isJson ? await res.json() : await res.text()
    };
}

/**
 * Light fetch wrapper for api. Used to upload files.
 */
export async function apiUploadFiles<TResponse = unknown>(url: string, method: method, data: FormData, signal?: AbortSignal): Promise<ApiResponse<TResponse>> {
    if (!appConfig.apiRootUrl) {
        throw new Error("appConfig.apiRootUrl not defined");
    }

    if (method === "GET" || method === "DELETE") {
        throw new Error("Method not supported with this api call");
    }

    await refreshAuthentication();
    const apiUrl = appConfig.apiRootUrl;
    const res = await fetch(`${apiUrl}api/${url}`, {
        method,
        body: data,
        headers: {
            "Accept": "application/json",
            "Authorization": `Bearer ${await Session.getAccessToken()}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept",
            "Scope": UserPermissionScope.Mobile.toString(),
        },
        signal,
    });

    const isJson = res.headers.get("content-type")?.includes("application/json");

    if (!res.ok) {
        throw isJson ? await res.json() : await res.text();
    }

    return {
        status: res.status,
        statusText: res.statusText,
        data: isJson ? await res.json() : await res.text()
    };
}

/**
 * Light fetch wrapper for api. Used when expecting blob from response.
 */
export async function apiCallBlob<TParams = unknown>(url: string, method: method, data?: TParams, signal?: AbortSignal) {
    if (!appConfig.apiRootUrl) {
        throw new Error("appConfig.apiRootUrl not defined");
    }

    if (method === "GET" && data) {
        url = handleGetInit(url, data);
    }

    await refreshAuthentication();
    const res = await fetch(`${appConfig.apiRootUrl}api/${url}`, {
        method,
        body: (method !== "GET" && data) ? JSON.stringify(data) : null,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${await Session.getAccessToken()}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept",
            "Scope": UserPermissionScope.Mobile.toString(),
        },
        signal,
    });

    if (!res.ok) {
        const isJson = res.headers.get("content-type")?.includes("application/json");
        throw isJson ? await res.json() : await res.text();
    }

    return {
        status: res.status,
        statusText: res.statusText,
        data: await res.blob()
    };
}
